import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Button as MuiButton,
  CircularProgress,
} from "@mui/material";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { useDesignForm } from "@/features/dmManagement/hooks/useDesignForm";
import { ComponentFileUpload } from "@/features/dmManagement/components/ComponentFileUpload";
import { CTextField } from "@/components/TextField/CTextField";
import { CSelect } from "@/components/Select/CSelect";
import type { ProductCategory } from "@/features/dmManagement/types";
import type { ISelectObject } from "@/types/select";

interface DesignFormProps {
  categories: ProductCategory[];
}

/**
 * デザイン登録フォーム
 * 識別番号・識別名・商品仕様の入力とPDFファイルアップロードによるデザイン申請を行う
 */
export const DesignForm: React.FC<DesignFormProps> = ({ categories }) => {
  const {
    form,
    isSubmitting,
    canSubmit,
    componentFields,
    productSelections,
    fileErrors,
    handleFileChange,
    handleProductChange,
    handleFormSubmit,
  } = useDesignForm(categories);

  const categoryOptions: ISelectObject[] = useMemo(
    () =>
      categories.map((c) => ({
        value: String(c.id),
        label: `${c.name} — ¥${c.unitPrice.toLocaleString("ja-JP")}/通`,
      })),
    [categories]
  );

  return (
    <Box
      component="form"
      onSubmit={handleFormSubmit}
      noValidate
      sx={{ maxWidth: 600 }}
    >
      <Stack spacing={3}>
        <CTextField
          control={form.control}
          name="taskId"
          label="識別番号"
          type="number"
          required
          size={SizingWrapperStyle.INHERIT}
        />

        <CTextField
          control={form.control}
          name="taskName"
          label="識別名"
          required
          size={SizingWrapperStyle.INHERIT}
        />

        <CSelect
          control={form.control}
          name="categoryId"
          label="商品仕様"
          options={categoryOptions}
          size={SizingWrapperStyle.INHERIT}
        />

        {componentFields.map((field) => (
          <ComponentFileUpload
            key={field.seq}
            label={field.label}
            seq={field.seq}
            products={field.products}
            selectedProductId={productSelections[field.seq]}
            onProductChange={(productId) => {
              handleProductChange(field.seq, productId);
            }}
            onFileChange={(file) => {
              handleFileChange(field.seq, file);
            }}
            error={fileErrors[field.seq]}
          />
        ))}

        <Box>
          <Typography variant="body2" color="text.secondary">
            ※ 審査には2〜5営業日かかります
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ※ PDFのみ、最大2GB
          </Typography>
        </Box>

        <MuiButton
          type="submit"
          variant="contained"
          disabled={!canSubmit || isSubmitting}
        >
          {isSubmitting && <CircularProgress size={20} sx={{ mr: 1 }} />}
          登録する
        </MuiButton>
      </Stack>
    </Box>
  );
};
